import type { Point } from "../types";

export interface RoomPolygon {
  points: Point[];
}

interface Edge {
  from: Point;
  to: Point;
}

const keyOf = (point: Point): string => `${point.x},${point.y}`;

const edgesOf = (polygon: RoomPolygon): Edge[] =>
  polygon.points.map((from, index) => ({
    from,
    to: polygon.points[(index + 1) % polygon.points.length]
  }));

/** Two rooms from the same wall graph share an edge when one runs a wall
 *  the other runs in the opposite direction. */
export const sharesEdge = (a: RoomPolygon, b: RoomPolygon): boolean => {
  const own = new Set(
    edgesOf(a).map((edge) => `${keyOf(edge.from)}|${keyOf(edge.to)}`)
  );
  return edgesOf(b).some(
    (edge) =>
      own.has(`${keyOf(edge.to)}|${keyOf(edge.from)}`) ||
      own.has(`${keyOf(edge.from)}|${keyOf(edge.to)}`)
  );
};

/** Absolute polygon area (shoelace). */
export const polygonArea = (points: Point[]): number =>
  Math.abs(signedArea(points));

const signedArea = (points: Point[]): number => {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
};

/**
 * Outline of several adjacent rooms as one polygon: the walls the rooms
 * share cancel out (each appears once in each direction) and what is left
 * chains into the outer boundary. Rooms that enclose another leave an
 * inner loop too; the largest loop is the outline. Returns null when the
 * remaining edges do not chain, which happens for rooms that only touch at
 * a corner.
 */
export const unionRoomPolygons = (rooms: RoomPolygon[]): Point[] | null => {
  if (rooms.length === 1) return rooms[0].points;
  const remaining = new Map<string, Edge>();
  let shared = 0;
  for (const room of rooms) {
    for (const edge of edgesOf(room)) {
      const reverse = `${keyOf(edge.to)}|${keyOf(edge.from)}`;
      if (remaining.delete(reverse)) {
        shared += 1;
        continue;
      }
      remaining.set(`${keyOf(edge.from)}|${keyOf(edge.to)}`, edge);
    }
  }
  if (shared === 0) return null;
  const byFrom = new Map<string, Edge[]>();
  for (const edge of remaining.values()) {
    const list = byFrom.get(keyOf(edge.from)) ?? [];
    list.push(edge);
    byFrom.set(keyOf(edge.from), list);
  }
  const loops: Point[][] = [];
  const used = new Set<Edge>();
  for (const start of remaining.values()) {
    if (used.has(start)) continue;
    const loop: Point[] = [];
    let edge: Edge | undefined = start;
    while (edge && !used.has(edge)) {
      used.add(edge);
      loop.push(edge.from);
      const candidates: Edge[] = byFrom.get(keyOf(edge.to)) ?? [];
      edge = candidates.find((candidate: Edge) => !used.has(candidate));
    }
    if (loop.length >= 3 && keyOf(loop[0]) === keyOf(start.from)) {
      loops.push(dropCollinear(loop));
    }
  }
  if (loops.length === 0) return null;
  return loops.reduce((largest, loop) =>
    Math.abs(signedArea(loop)) > Math.abs(signedArea(largest)) ? loop : largest
  );
};

/** The wall graph splits walls where other walls meet them, so a merged
 *  outline carries corners that are not corners; those go. */
const dropCollinear = (points: Point[]): Point[] =>
  points.filter((point, index) => {
    const previous = points[(index + points.length - 1) % points.length];
    const next = points[(index + 1) % points.length];
    const cross =
      (point.x - previous.x) * (next.y - point.y) -
      (point.y - previous.y) * (next.x - point.x);
    return Math.abs(cross) > 1e-6;
  });
